import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { debugValue } from "@/env/utils";
import { DEFAULT_LANGUAGE, i18n } from "@/i18n";
import { Capacitor } from "@/lib/capacitor";
import { Device } from "@/lib/capacitor/device";
import { createSelectors } from "@/lib/store/utils/create-selectors";
import { CapacitorStorage } from "@/lib/store/utils/storage";

/**
 * Which appearance the employee asked for.
 *
 * Three-way rather than a boolean, and `system` is the default: on a phone the appearance is
 * already a decision the owner made once, for every app at once, and following it is what the
 * platform expects. `light` and `dark` are for the employees who want this app to disagree with
 * that — a bar shift at midnight is not the moment to discover the app only listens to the OS.
 *
 * Unlike `appReworkOptOut` this can point both ways, because both directions are safe: it changes
 * how the app looks, never which app it is. Read in `useAppearance()` and nowhere else.
 */
export type Appearance = "system" | "light" | "dark";

export type PreferencesState = {
  language: "system" | Language;
  reducedHaptics: boolean;
  appearance: Appearance;
  /**
   * Whether this employee has put themselves back on the app as it was.
   *
   * One direction only, which is why this is a boolean and not a three-way `auto | new | old`: it
   * can turn off what the organization turned on, never the reverse. A switch that could turn the
   * herziening _on_ would put an employee on an app their organization has never seen, and nothing
   * in the admin would explain what they are looking at — the support call lands on a planner who
   * cannot place it.
   *
   * Read in `useAppReworkFlags()` and nowhere else, so there stays exactly one place that decides
   * which half of the app renders.
   */
  appReworkOptOut: boolean;
  /**
   * Wanneer de instelkaart op Vandaag is weggeklikt, als ISO-tijdstip.
   *
   * Een tijdstip en geen `true`, omdat wegklikken hier "niet nu" betekent en niet "nooit meer".
   * Meldingen en een gekoppelde agenda zijn de twee dingen waardoor de app je op tijd bereikt; één
   * keer wegtikken op een druk moment hoort dat niet voorgoed af te sluiten. Zie `use-setup-nudges`
   * voor hoe lang hij dan wegblijft.
   */
  setupNudgeDismissedAt: string | null;
};

export type PreferencesActions = {
  setLanguage: (language: PreferencesState["language"]) => void;
  setReducedHaptics: (reducedHaptics: PreferencesState["reducedHaptics"]) => void;
  setAppReworkOptOut: (appReworkOptOut: PreferencesState["appReworkOptOut"]) => void;
  setAppearance: (appearance: PreferencesState["appearance"]) => void;
  dismissSetupNudge: (at: string) => void;
};

export const usePreferencesStore = create<PreferencesState & PreferencesActions>()(
  devtools(
    persist(
      (set) => ({
        // State
        language: "system",
        reducedHaptics: false,
        appReworkOptOut: false,
        appearance: "system",
        setupNudgeDismissedAt: null,

        // Actions
        setLanguage: (language) => {
          set({ language });
          enforceLanguage(language);
        },
        setReducedHaptics: (reducedHaptics) => {
          set({ reducedHaptics });
        },
        setAppReworkOptOut: (appReworkOptOut) => {
          set({ appReworkOptOut });
        },
        setAppearance: (appearance) => {
          set({ appearance });
        },
        dismissSetupNudge: (at) => {
          set({ setupNudgeDismissedAt: at });
        },
      }),
      {
        name: "preferences",
        storage: createJSONStorage(() => CapacitorStorage),
        onRehydrateStorage: () => (state) => {
          if (state?.language) {
            state.setLanguage(state?.language);
          }
          if (state?.reducedHaptics) {
            state.setReducedHaptics(state.reducedHaptics);
          }
        },
      },
    ),
    {
      enabled: debugValue("store") && Capacitor.Platform === "web",
    },
  ),
);

export const useSelectorPreferencesStore = createSelectors(usePreferencesStore);

export async function getComputedLanguage(
  language: PreferencesState["language"],
): Promise<Language> {
  switch (language) {
    case "system":
      return Device.getLanguageCode().then(({ value }) =>
        i18n.languages.includes(value) ? (value as Language) : DEFAULT_LANGUAGE,
      );
    default:
      return language;
  }
}

export async function enforceLanguage(language: PreferencesState["language"]) {
  switch (language) {
    case "system":
      Device.getLanguageCode().then(({ value }) => {
        enforceLanguage(i18n.languages.includes(value) ? (value as Language) : DEFAULT_LANGUAGE);
      });
      break;
    default:
      i18n.changeLanguage(language);
      break;
  }
}
